"use client";

import { useCallback, useEffect, useRef } from "react";
import { handleSortAndSavedWork } from "@/app/actions/actions";
import { normalizeSavedWorkData } from "@/app/(protected)/saved-projects/components/SavedProjectsWrapper/utils";
import { Asset, TAB_TYPES, ResponseData, VideoGeneratedOutputs } from "@/app/(protected)/saved-projects/types";
import { PROJECT_TYPES, PROJECT_COUNT_LOADER_ALLOWED_PAGES, URLS, BrandId } from "@/constants/constants";
import { Actions, State } from "@/app/context/AppProvider/reducer";
import { setIsNewProjectSaved, setLoading } from "@/app/context/AppProvider/actions";
import { logger } from "@/lib/client-logger";

type SavedWorkParams = {
  brandId: BrandId;
  brandWithLocation: string;
  isNotOreo: boolean;
  isAdmin: boolean;
  hasSession: boolean;
  dispatch: React.Dispatch<Actions>;
  state: State;
  pathname: string;
};

export const useSavedWork = ({
  brandId,
  brandWithLocation,
  isNotOreo,
  isAdmin,
  hasSession,
  dispatch,
  state,
  pathname,
}: SavedWorkParams) => {
  const brandIdChangeRef = useRef<string>("");
  const prevPathnameRef = useRef<string>("");

  const fetchSavedWork = useCallback(async () => {
    try {
      const archivedQueryParam = { onlyArchived: true };
      const savedAssetsParams = isNotOreo ? { type: PROJECT_TYPES.GENERATE_ASSETS } : { includeAllGeneratedOutputs: true };
      const projectsParams = isNotOreo ? {} : { isSaveAndClose: true };
      /**
       * To prevent the project API call if the user is admin
       */
      if (isAdmin) {
        return;
      }

      const showProjectFetchLoader =
        PROJECT_COUNT_LOADER_ALLOWED_PAGES.includes(location.pathname) &&
        (brandIdChangeRef.current === "" || brandIdChangeRef.current === brandId);

      if (showProjectFetchLoader) {
        brandIdChangeRef.current = brandId;
        dispatch(setLoading(true));
      }
      if (brandIdChangeRef.current !== brandId) {
        brandIdChangeRef.current = brandId;
      }

      const [archivedData, assetData, nonArchivedData] = await Promise.all([
        handleSortAndSavedWork(archivedQueryParam),
        handleSortAndSavedWork(savedAssetsParams),
        handleSortAndSavedWork(projectsParams),
      ]);

      if (showProjectFetchLoader) {
        dispatch(setLoading(false));
      }

      if (!archivedData || !nonArchivedData) return;

      // Filter out video projects without completed video in saved asset tab
     const filteredProjects = (assetData?.data || []).filter((item: ResponseData) => {
        if (item.type === PROJECT_TYPES.GENERATE_VIDEO) {
          return !!(item.generated_outputs as VideoGeneratedOutputs)?.modelOutputs?.videos?.[0];
        }
  
        if (item.type === PROJECT_TYPES.STORYBOARD) {
          const scenes = item?.generated_outputs?.modelOutputs?.scenes ?? [];
          return scenes.length > 0;
        }
  
        return true;
      });

      dispatch({
        type: "SET_SAVED_WORK",
        payload: {
          projects: normalizeSavedWorkData(nonArchivedData.data, TAB_TYPES.PROJECTS),
          savedAssets: normalizeSavedWorkData(filteredProjects, TAB_TYPES.ASSETS, brandWithLocation) as Asset[],
          archive: normalizeSavedWorkData(archivedData.data, TAB_TYPES.ARCHIVE),
          totalProjects: nonArchivedData.data?.length || 0,
          pagination: {
            projects: nonArchivedData.pager,
            assets: assetData.pager,
            archive: archivedData.pager,
          },
          allSavedProjectsData: assetData?.data,
        },
      });
    } catch (error) {
      logger.error("Error fetching saved work:", error);
    }
  }, [brandId, dispatch, isNotOreo, isAdmin, brandWithLocation]);

  // Initial fetch of saved work when the component mounts
  useEffect(() => {
    if (!hasSession) return;
    fetchSavedWork();
  }, [fetchSavedWork, hasSession]);

  // Refetch saved work when navigating to saved projects/home or when a new project gets saved
  useEffect(() => {
    const allowProjectRefetch = [URLS.HOME, URLS.SAVED_PROJECTS].some((route) => pathname?.startsWith(route));

    if (!allowProjectRefetch) {
      prevPathnameRef.current = pathname;
      return;
    }

    const isPathChanged = prevPathnameRef.current !== pathname && prevPathnameRef.current !== "";
    const isNewProjectSaved = state.isNewProjectSaved;

    if (isPathChanged || isNewProjectSaved) {
      fetchSavedWork();
      if (isNewProjectSaved) {
        dispatch(setIsNewProjectSaved(false));
      }
    }

    prevPathnameRef.current = pathname;
  }, [state.isNewProjectSaved, pathname, fetchSavedWork, dispatch]);
};
